import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

import Layout from "@/components/app/Layout";
import { MENU } from "@/components/app/menuConfig";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import DriversPage from "@/pages/DriversPage";
import VehiclesPage from "@/pages/VehiclesPage";
import KmLogsPage from "@/pages/KmLogsPage";
import PersonnelPage from "@/pages/PersonnelPage";
import SalariesPage from "@/pages/SalariesPage";
import SuppliersPage from "@/pages/SuppliersPage";
import FuelPage from "@/pages/FuelPage";
import InvoicesPage from "@/pages/InvoicesPage";
import IncomingPaymentsPage from "@/pages/IncomingPaymentsPage";
import CompaniesPage from "@/pages/CompaniesPage";
import TendersPage from "@/pages/TendersPage";
import ReportsPage from "@/pages/ReportsPage";
import UsersPage from "@/pages/UsersPage";
import SalesInvoicesPage from "@/pages/SalesInvoicesPage";
import VehicleEarningsPage from "@/pages/VehicleEarningsPage";
import OffersPage from "@/pages/OffersPage";
import CustomerPersonnelPage from "@/pages/CustomerPersonnelPage";
import TransportRoutesPage from "@/pages/TransportRoutesPage";
import FuelImpactPage from "@/pages/FuelImpactPage";
import PuantajPage from "@/pages/PuantajPage";
import KarZararPage from "@/pages/KarZararPage";
import MailReportPage from "@/pages/MailReportPage";
import FinancialReportsPage from "@/pages/FinancialReportsPage";
import YearlyConsolidatedPage from "@/pages/YearlyConsolidatedPage";
import ExtraExpensesPage from "@/pages/ExtraExpensesPage";
import BankTransactionsPage from "@/pages/BankTransactionsPage";
import MarketingPage from "@/pages/MarketingPage";
import InvoiceMatchingPage from "@/pages/InvoiceMatchingPage";
import SupplierAccountsPage from "@/pages/SupplierAccountsPage";
import CustomerAccountsPage from "@/pages/CustomerAccountsPage";
import InvoicesMonthlyPage from "@/pages/InvoicesMonthlyPage";
import HakedisComparisonPage from "@/pages/HakedisComparisonPage";
import SchoolPaymentsPage from "@/pages/SchoolPaymentsPage";
import AiAssistantPage from "@/pages/AiAssistantPage";
import TenderTrackingPage from "@/pages/TenderTrackingPage";
import PortalPage from "@/pages/PortalPage";
import DriverTaskPage from "@/pages/DriverTaskPage";
import RouteSharePage from "@/pages/RouteSharePage";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Yükleniyor...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "supplier") return <Navigate to="/portal" replace />;
  if (user.role !== "admin") {
    const restricted = Array.isArray(user.allowed_pages) && user.allowed_pages.length > 0;
    const canSee = (m) => m.roles.includes(user.role) && (!restricted || user.allowed_pages.includes(m.to));
    const item = MENU.find((m) => m.to === location.pathname);
    if (item && !canSee(item)) {
      const first = MENU.find(canSee);
      if (first && first.to !== location.pathname) return <Navigate to={first.to} replace />;
    }
  }
  return <Layout>{children}</Layout>;
}

function PortalProtected() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Yükleniyor...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "supplier") return <Navigate to="/" replace />;
  return <PortalPage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/gorev/:token" element={<DriverTaskPage />} />
      <Route path="/paylas/:token" element={<RouteSharePage />} />
      <Route path="/portal" element={<PortalProtected />} />
      <Route path="/" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/drivers" element={<Protected><DriversPage /></Protected>} />
      <Route path="/vehicles" element={<Protected><VehiclesPage /></Protected>} />
      <Route path="/km-logs" element={<Protected><KmLogsPage /></Protected>} />
      <Route path="/personnel" element={<Protected><PersonnelPage /></Protected>} />
      <Route path="/salaries" element={<Protected><SalariesPage /></Protected>} />
      <Route path="/suppliers" element={<Protected><SuppliersPage /></Protected>} />
      <Route path="/fuel" element={<Protected><FuelPage /></Protected>} />
      <Route path="/invoices" element={<Protected><InvoicesPage /></Protected>} />
      <Route path="/sales-invoices" element={<Protected><SalesInvoicesPage /></Protected>} />
      <Route path="/vehicle-earnings" element={<Protected><VehicleEarningsPage /></Protected>} />
      <Route path="/incoming" element={<Protected><IncomingPaymentsPage /></Protected>} />
      <Route path="/companies" element={<Protected><CompaniesPage /></Protected>} />
      <Route path="/tenders" element={<Protected><TendersPage /></Protected>} />
      <Route path="/tender-tracking" element={<Protected><TenderTrackingPage /></Protected>} />
      <Route path="/offers" element={<Protected><OffersPage /></Protected>} />
      <Route path="/customer-personnel" element={<Protected><CustomerPersonnelPage /></Protected>} />
      <Route path="/transport-routes" element={<Protected><TransportRoutesPage /></Protected>} />
      <Route path="/fuel-impact" element={<Protected><FuelImpactPage /></Protected>} />
      <Route path="/puantaj" element={<Protected><PuantajPage /></Protected>} />
      <Route path="/kar-zarar" element={<Protected><KarZararPage /></Protected>} />
      <Route path="/mail-report" element={<Protected><MailReportPage /></Protected>} />
      <Route path="/financial-reports" element={<Protected><FinancialReportsPage /></Protected>} />
      <Route path="/yearly-consolidated" element={<Protected><YearlyConsolidatedPage /></Protected>} />
      <Route path="/extra-expenses" element={<Protected><ExtraExpensesPage /></Protected>} />
      <Route path="/bank-transactions" element={<Protected><BankTransactionsPage /></Protected>} />
      <Route path="/marketing" element={<Protected><MarketingPage /></Protected>} />
      <Route path="/invoice-matching" element={<Protected><InvoiceMatchingPage /></Protected>} />
      <Route path="/supplier-accounts" element={<Protected><SupplierAccountsPage /></Protected>} />
      <Route path="/customer-accounts" element={<Protected><CustomerAccountsPage /></Protected>} />
      <Route path="/invoices-monthly" element={<Protected><InvoicesMonthlyPage /></Protected>} />
      <Route path="/hakedis-comparison" element={<Protected><HakedisComparisonPage /></Protected>} />
      <Route path="/school-payments" element={<Protected><SchoolPaymentsPage /></Protected>} />
      <Route path="/ai-assistant" element={<Protected><AiAssistantPage /></Protected>} />
      <Route path="/reports" element={<Protected><ReportsPage /></Protected>} />
      <Route path="/users" element={<Protected><UsersPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
